import { Box, LoadingOverlay, Paper, Table, Text } from "@mantine/core"
import { ArrowDownIcon, ArrowUpIcon } from "@phosphor-icons/react"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { CardHeader } from "../../parts/CardHeader"
import dash from "../../real.module.css"
import classes from "../analytics.module.css"

// Ord_Dashboard card: white, slate border, title + meta line, optional right slot.
type CardProps = {
  title: string
  subtitle?: string
  /** @dehub/help `section` key: «?» after the title. */
  helpKey?: string
  right?: ReactNode
  isLoading?: boolean
  children: ReactNode
}

export function AnalyticsCard({ title, subtitle, helpKey, right, isLoading, children }: CardProps) {
  return (
    <Paper className={dash.card} pos="relative">
      <LoadingOverlay visible={!!isLoading} />
      <CardHeader title={title} subtitle={subtitle} helpKey={helpKey} right={right} />
      {children}
    </Paper>
  )
}

export function EmptyRow() {
  const { t } = useTranslation(["dashboard"])
  return <Text className={classes.empty}>{t("analytics.emptyState", { defaultValue: "Өгөгдөл байхгүй" })}</Text>
}

/** Arrow + figure, always neutral: colour on the dashboard is kept for status. */
export function Delta({ sign, text }: { sign: "up" | "down" | "flat"; text: string }) {
  const Icon = sign === "up" ? ArrowUpIcon : sign === "down" ? ArrowDownIcon : null
  return (
    <Box component="span" className={classes.delta}>
      {Icon ? <Icon size={14} weight="bold" /> : null}
      {text}
    </Box>
  )
}

/** Breakdown table shown under an opened row. The last column is right-aligned. */
export function DrillTable({ headers, rows }: { headers: string[]; rows: ReactNode[][] }) {
  return (
    <Table className={classes.table} withRowBorders={false}>
      <Table.Thead>
        <Table.Tr>
          {headers.map((h, i) => (
            <Table.Th key={i} data-align={i === headers.length - 1 ? "right" : undefined}>{h}</Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row, ri) => (
          <Table.Tr key={ri}>
            {row.map((cell, ci) => (
              <Table.Td key={ci} data-align={ci === row.length - 1 ? "right" : undefined}>{cell}</Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

export function DrillTitle({ children }: { children: ReactNode }) {
  return <Text className={classes.drillTitle}>{children}</Text>
}
